import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RelatedProperties = ({ currentProperty, relatedProperties }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  if (!relatedProperties || relatedProperties?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Home" size={48} color="var(--color-text-secondary)" className="mx-auto mb-4" />
        <h2 className="text-xl font-heading font-bold text-text-primary mb-2">
          Aucune propriété similaire trouvée
        </h2>
        <p className="text-text-secondary mb-6">
          Explorez notre catalogue complet pour découvrir d'autres opportunités
        </p>
        <Link to="/property-search-listings">
          <Button variant="default" iconName="Search" iconPosition="left">
            Voir toutes les propriétés
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-2">
            Propriétés similaires
          </h2>
          <p className="text-text-secondary">
            {currentProperty?.communes?.name && (
              <>Découvrez d'autres propriétés à {currentProperty?.communes?.name} et dans les quartiers similaires</>
            )}
          </p>
        </div>
        <Link to="/property-search-listings" className="hidden md:block">
          <Button variant="outline" iconName="ArrowRight" iconPosition="right">
            Voir toutes
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {relatedProperties?.slice(0, 6)?.map((property) => (
          <Link
            key={property?.id}
            to={`/individual-property-page/${property?.id}`}
            className="group bg-card rounded-lg border border-border overflow-hidden hover:shadow-warm transition-all duration-300"
          >
            {/* Property Image */}
            <div className="relative aspect-video overflow-hidden">
              <Image
                src={property?.images?.[0] || '/assets/images/no_image.png'}
                alt={property?.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              
              {/* Property Type Badge */}
              <div className="absolute top-4 left-4">
                <span className="px-2 py-1 bg-brand-gold text-white text-xs font-medium rounded-full capitalize">
                  {property?.property_type}
                </span>
              </div>

              {/* Price Badge */}
              <div className="absolute bottom-4 right-4">
                <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1">
                  <span className="text-brand-earth font-bold text-sm">
                    {formatPrice(property?.price)}
                  </span>
                </div>
              </div>

              {/* Video Indicator */}
              {property?.videos && property?.videos?.length > 0 && (
                <div className="absolute top-4 right-4">
                  <div className="bg-black/60 backdrop-blur-sm rounded-full p-2">
                    <Icon name="Play" size={16} color="white" />
                  </div>
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="p-4">
              <div className="mb-3">
                <h3 className="font-heading font-semibold text-lg text-text-primary line-clamp-2 mb-1 group-hover:text-brand-gold transition-colors">
                  {property?.title}
                </h3>
                <div className="flex items-center text-text-secondary text-sm">
                  <Icon name="MapPin" size={14} className="mr-1" />
                  <span>{property?.communes?.name || property?.address}</span>
                </div>
              </div>

              {/* Property Features */}
              <div className="flex items-center justify-between text-sm text-text-secondary mb-4">
                {property?.bedrooms && (
                  <div className="flex items-center">
                    <Icon name="Bed" size={14} className="mr-1" />
                    <span>{property?.bedrooms}</span>
                  </div>
                )}
                {property?.bathrooms && (
                  <div className="flex items-center">
                    <Icon name="Bath" size={14} className="mr-1" />
                    <span>{property?.bathrooms}</span>
                  </div>
                )}
                {property?.surface_area && (
                  <div className="flex items-center">
                    <Icon name="Square" size={14} className="mr-1" />
                    <span>{property?.surface_area}m²</span>
                  </div>
                )}
              </div>

              {/* Price per sqm */}
              {property?.surface_area && (
                <div className="text-right text-xs text-text-secondary">
                  {formatPrice(property?.price / property?.surface_area)}/m²
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-brand-gold/10 to-brand-earth/10 rounded-lg p-6 text-center">
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Vous ne trouvez pas ce que vous cherchez ?
        </h3>
        <p className="text-text-secondary mb-6">
          Explorez notre catalogue complet ou contactez-nous pour une recherche personnalisée
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/property-search-listings">
            <Button variant="default" iconName="Search" iconPosition="left">
              Explorer le catalogue
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" iconName="MessageCircle" iconPosition="left">
              Contact personnalisé
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RelatedProperties;